"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const industries = [{
  id: "ind-1",
  name: "HVAC & Plumbing",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_hvac_plumbing_industry.png"
}, {
  id: "ind-2",
  name: "Legal Services",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_legal_services_industry.png"
}, {
  id: "ind-3",
  name: "Retail & Ecommerce",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_retail_ecommerce_industry.png"
}, {
  id: "ind-4",
  name: "Roofing & Exteriors",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_roofing_exteriors_industry.png"
}, {
  id: "ind-5",
  name: "Dental & Medical",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_dental_medical_industry.png"
}, {
  id: "ind-6",
  name: "Real Estate",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_real_estate_industry.png"
}, {
  id: "ind-7",
  name: "Landscaping",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_landscaping_industry.png"
}, {
  id: "ind-8",
  name: "Home Services",
  logo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1586456/img/logo_home_services_industry.png"
}];
export default function PartnersInGrowth() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="partners-in-growth" className="bg-background py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div ref={headerRef} className={`mb-16 text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-sans text-3xl font-semibold text-primary md:text-4xl">
            Businesses We Help
          </h2>
          <p className="mx-auto mt-4 max-w-2xl font-sans text-base text-muted-foreground">
            We partner with local service companies and growing businesses to drive measurable revenue through strategic digital marketing.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-4 transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {industries.map((industry, i) => <div key={industry.id} data-index={i} className="group flex cursor-pointer flex-col items-center justify-center gap-4 p-6 transition-all duration-300 hover:bg-card hover:shadow-sm">
              <div className="flex h-20 w-full items-center justify-center">
                <img src={industry.logo} alt={`${industry.name} logo`} className="max-h-12 w-auto object-contain opacity-50 grayscale transition-all duration-300 group-hover:opacity-100 group-hover:grayscale-0" />
              </div>
              <span className="text-center font-sans text-sm font-medium text-muted-foreground transition-colors duration-300 group-hover:text-primary">
                {industry.name}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}